/**
 * Calls to the hub's teams module (apps/hub/src/ext/teams). In demo mode (?mock=1) they
 * change the demo slice in memory with the same rules, so every screen works without a hub.
 * The hub answers with the change; the live slice arrives over SSE.
 */
import { ALL_PROJECTS_WORKSPACE_ID, DEFAULT_ORG_ID, defaultMemberPrefs, expandTargets, parseMentions, refTypeOfKey, routeRequest } from '@orgus/protocol';
import type { Assignment, MemberPrefs, ProjectOwnership, RouteDecision, RouteInput, RoutingSettings, Team, TeamsState, Workspace } from '@orgus/protocol';
import { ApiError, api, apiDelete, apiGet, apiPost } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { MOCK_ME_LOGIN } from '../auth/mockAuth.ts';
import { updateDemo, useTeamsDemo } from './model.ts';

const isMock = () => useOrgus.getState().mode === 'mock';
const later = <T,>(v: () => T, ms = 200): Promise<T> =>
  new Promise((resolve, reject) =>
    setTimeout(() => {
      try {
        resolve(v());
      } catch (e) {
        reject(e);
      }
    }, ms),
  );
const fail = (what: string, why: string, next: string) => new ApiError({ what, why, next, status: 400 }, 400);
const demo = () => useTeamsDemo.getState().state;
const id = (p: string) => `${p}_${Math.random().toString(36).slice(2, 10)}`;
const enc = encodeURIComponent;

export interface AssignInput {
  key: string;
  title: string;
  projectId?: string | null;
  to: string | string[];
  note?: string;
}

function mockAssign(b: AssignInput): Assignment {
  const s = demo();
  const handles = typeof b.to === 'string' ? parseMentions(b.to) : b.to.map((x) => x.replace(/^@/, '').toLowerCase());
  if (!handles.length) throw fail('Nobody was named.', 'An assignment needs at least one @person or @team.', 'Type @ and pick someone.');
  for (const h of handles) {
    if (!s.teams.some((t) => t.handle === h) && !s.people.some((p) => p.login === h)) {
      throw fail(`Nobody called @${h} is on this hub.`, 'Items go to people who can sign in, or to a team.', 'Check the spelling.');
    }
  }
  const a: Assignment = {
    key: b.key,
    refType: refTypeOfKey(b.key) ?? 'session',
    title: b.title,
    projectId: b.projectId ?? null,
    assignees: expandTargets(handles, s.teams),
    by: MOCK_ME_LOGIN,
    at: Date.now(),
    reason: 'mention',
    level: 0,
    why: `Assigned by @${MOCK_ME_LOGIN}`,
    ...(b.note ? { note: b.note } : {}),
  };
  updateDemo((x) => ({ ...x, assignments: [a, ...x.assignments.filter((y) => y.key !== a.key)] }));
  return a;
}

export const teamsClient = {
  assign: (b: AssignInput): Promise<{ assignment: Assignment }> => (isMock() ? later(() => ({ assignment: mockAssign(b) })) : apiPost('/api/teams/assign', b)),

  /** A session becomes yours whatever it is doing; the assignment is null when nothing waits now. */
  claim: (key: string, title: string, projectId: string | null): Promise<{ assignment: Assignment | null }> =>
    isMock()
      ? later(() => {
          const a: Assignment = { key, refType: refTypeOfKey(key) ?? 'session', title, projectId, assignees: [MOCK_ME_LOGIN], by: MOCK_ME_LOGIN, at: Date.now(), reason: 'claimed', level: 0, why: `Claimed by @${MOCK_ME_LOGIN}`, ackedBy: MOCK_ME_LOGIN, ackedAt: Date.now() };
          updateDemo((x) => ({
            ...x,
            assignments: [a, ...x.assignments.filter((y) => y.key !== key)],
            sessionOwners: key.startsWith('s:') ? { ...x.sessionOwners, [key.slice(2)]: MOCK_ME_LOGIN } : x.sessionOwners,
          }));
          return { assignment: a };
        })
      : apiPost('/api/teams/claim', { key, title, projectId }),

  ack: (key: string): Promise<{ assignment: Assignment }> =>
    isMock()
      ? later(() => {
          const a = { ...demo().assignments.find((x) => x.key === key)!, ackedBy: MOCK_ME_LOGIN, ackedAt: Date.now() };
          updateDemo((x) => ({ ...x, assignments: x.assignments.map((y) => (y.key === key ? a : y)) }));
          return { assignment: a };
        })
      : apiPost(`/api/teams/assignments/${enc(key)}/ack`),

  unassign: (key: string): Promise<unknown> =>
    isMock() ? later(() => updateDemo((x) => ({ ...x, assignments: x.assignments.filter((y) => y.key !== key) }))) : apiDelete(`/api/teams/assignments/${enc(key)}`),

  route: (input: RouteInput): Promise<RouteDecision> =>
    isMock() ? later(() => routeRequest(input, { ...demo(), now: Date.now() }), 80) : apiPost('/api/teams/route', input),

  prefs: (): Promise<MemberPrefs> =>
    isMock() ? later(() => useTeamsDemo.getState().prefs[MOCK_ME_LOGIN] ?? defaultMemberPrefs(MOCK_ME_LOGIN), 150) : apiGet('/api/teams/prefs'),

  setPrefs: (patch: Partial<Pick<MemberPrefs, 'events' | 'minUrgency' | 'quietHours'>>): Promise<MemberPrefs> =>
    isMock()
      ? later(() => {
          const cur = useTeamsDemo.getState().prefs[MOCK_ME_LOGIN] ?? defaultMemberPrefs(MOCK_ME_LOGIN);
          const next: MemberPrefs = { ...cur, ...patch, events: { ...cur.events, ...(patch.events ?? {}) }, updatedAt: Date.now() };
          useTeamsDemo.setState((d) => ({ prefs: { ...d.prefs, [MOCK_ME_LOGIN]: next } }));
          return next;
        })
      : api('/api/teams/prefs', { method: 'PUT', body: patch }),

  renameOrg: (name: string): Promise<unknown> =>
    isMock() ? later(() => updateDemo((x) => ({ ...x, orgs: x.orgs.map((o) => (o.id === DEFAULT_ORG_ID ? { ...o, name } : o)) }))) : api('/api/teams/org', { method: 'PUT', body: { name } }),

  createWorkspace: (name: string, projectIds: string[] | null): Promise<{ workspace: Workspace }> =>
    isMock()
      ? later(() => {
          if (demo().workspaces.some((w) => w.name.toLowerCase() === name.toLowerCase())) throw fail(`A workspace called ${name} already exists.`, 'Workspace names are unique.', 'Pick another name.');
          const workspace: Workspace = { id: id('wsp'), orgId: DEFAULT_ORG_ID, name, projectIds, builtIn: false, createdAt: Date.now(), createdBy: MOCK_ME_LOGIN };
          updateDemo((x) => ({ ...x, workspaces: [...x.workspaces, workspace] }));
          return { workspace };
        })
      : apiPost('/api/teams/workspaces', { name, projectIds }),

  updateWorkspace: (wsId: string, patch: { name?: string; projectIds?: string[] | null }): Promise<unknown> =>
    isMock() ? later(() => updateDemo((x) => ({ ...x, workspaces: x.workspaces.map((w) => (w.id === wsId ? { ...w, ...patch } : w)) }))) : api(`/api/teams/workspaces/${enc(wsId)}`, { method: 'PATCH', body: patch }),

  deleteWorkspace: (wsId: string): Promise<unknown> =>
    isMock() ? later(() => updateDemo((x) => ({ ...x, workspaces: x.workspaces.filter((w) => w.id !== wsId || w.id === ALL_PROJECTS_WORKSPACE_ID) }))) : apiDelete(`/api/teams/workspaces/${enc(wsId)}`),

  createTeam: (b: { name: string; handle?: string; members: string[] }): Promise<{ team: Team }> =>
    isMock()
      ? later(() => {
          const handle = (b.handle || b.name).toLowerCase().replace(/^@/, '').replace(/[^a-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');
          const s = demo();
          if (s.teams.some((t) => t.handle === handle)) throw fail(`@${handle} is already a team.`, 'Handles are unique so a mention names one team.', 'Pick another handle.');
          if (s.people.some((p) => p.login === handle)) throw fail(`@${handle} is already a person here.`, "A team handle cannot be someone's login.", 'Pick another handle.');
          const team: Team = { id: id('team'), orgId: DEFAULT_ORG_ID, name: b.name, handle, members: b.members, createdAt: Date.now(), createdBy: MOCK_ME_LOGIN };
          updateDemo((x) => ({ ...x, teams: [...x.teams, team] }));
          return { team };
        })
      : apiPost('/api/teams/teams', b),

  updateTeam: (teamId: string, patch: { name?: string; handle?: string; members?: string[] }): Promise<unknown> =>
    isMock() ? later(() => updateDemo((x) => ({ ...x, teams: x.teams.map((t) => (t.id === teamId ? { ...t, ...patch } : t)) }))) : api(`/api/teams/teams/${enc(teamId)}`, { method: 'PATCH', body: patch }),

  deleteTeam: (teamId: string): Promise<unknown> =>
    isMock()
      ? later(() =>
          updateDemo((x) => {
            const h = x.teams.find((t) => t.id === teamId)?.handle;
            return { ...x, teams: x.teams.filter((t) => t.id !== teamId), ownership: x.ownership.map((o) => ({ ...o, owners: o.owners.filter((w) => w !== `@${h}`) })) };
          }),
        )
      : apiDelete(`/api/teams/teams/${enc(teamId)}`),

  setOwnership: (projectId: string, b: Partial<Pick<ProjectOwnership, 'owners' | 'rota'>>): Promise<unknown> =>
    isMock()
      ? later(() =>
          updateDemo((x) => {
            const prev = x.ownership.find((o) => o.projectId === projectId);
            const next: ProjectOwnership = { projectId, owners: b.owners ?? prev?.owners ?? [], rota: b.rota === undefined ? (prev?.rota ?? null) : b.rota, updatedAt: Date.now(), updatedBy: MOCK_ME_LOGIN };
            return { ...x, ownership: [...x.ownership.filter((o) => o.projectId !== projectId), next] };
          }),
        )
      : api(`/api/teams/ownership/${enc(projectId)}`, { method: 'PUT', body: b }),

  setRouting: (b: Partial<RoutingSettings>): Promise<unknown> =>
    isMock() ? later(() => updateDemo((x: TeamsState) => ({ ...x, routing: { ...x.routing, ...b } }))) : api('/api/teams/routing', { method: 'PUT', body: b }),
};
